class PredictiveSearch extends SearchForm {
	constructor() {
		super();
		this.cachedResults = {};
		this.predictiveSearchResults = this.querySelectorAll(
			"[data-predictive-search]"
		);
		this.allPredictiveSearchInstances =
			document.querySelectorAll("predictive-search");
		this.isOpen = false;
		this.abortController = new AbortController();
		this.searchTerm = "";
		this.body = document.querySelector("body");
		this.headerBottom = document.querySelector(".header__bottom");
		this.promoModal = document.querySelector(".search__modal");
		this.headerInput = document.querySelector(".search-modal .search__input");
		this.setupEventListeners();
		this.preloadUpsell();
	}

	connectedCallback() {
		document.addEventListener("click", this.onOutsideClick);
		window.addEventListener("resize", this.onWindowResize);
	}

	disconnectedCallback() {
		document.removeEventListener("click", this.onOutsideClick);
		window.removeEventListener("resize", this.onWindowResize);
	}

	setupEventListeners() {
		this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));
		this.input.addEventListener("focus", this.onFocus.bind(this));
		this.addEventListener("focusout", this.onFocusOut.bind(this));
		this.addEventListener("keyup", this.onKeyup.bind(this));
		this.addEventListener("keydown", this.onKeydown.bind(this));
		// Delegated: the product grid is replaced via innerHTML on every fetch,
		// so listeners bound directly to the buttons would be lost each time.
		this.addEventListener("click", this.onCarouselClick.bind(this));
		this.addEventListener("click", this.onClearClick.bind(this));
		this.onOutsideClick = this.onOutsideClick.bind(this);
		this.onWindowResize = () => this.updateCarouselButtons();
		this.addEventListener(
			"scroll",
			(event) => {
				if (event.target.classList?.contains("predictive-search__results-list_products")) {
					this.updateCarouselButtons(event.target);
				}
			},
			true
		);
	}

	updateCarouselButtons(list) {
		list =
			list || this.querySelector(".predictive-search__results-list_products");
		if (!list) return;
		const nav = list
			.closest(".predictive-search__results")
			?.querySelector(".predictive-search__carousel-nav");
		if (!nav) return;
		const prev = nav.querySelector("[data-predictive-search-carousel-prev]");
		const next = nav.querySelector("[data-predictive-search-carousel-next]");
		const max = list.scrollWidth - list.clientWidth;
		if (prev) prev.disabled = list.scrollLeft <= 1;
		if (next) next.disabled = list.scrollLeft >= max - 1;
	}

	onClearClick(event) {
		if (!event.target.closest("[data-predictive-search-clear]")) return;
		event.preventDefault();
		this.searchTerm = "";
		this.abortController.abort();
		this.abortController = new AbortController();
		this.input.value = "";
		this.close(true);
		this.input.blur();
	}

	onOutsideClick(event) {
		if (
			!this.hasAttribute("open") ||
			this.classList.contains("search-modal__form")
		)
			return;
		if (!event.composedPath().includes(this)) this.close();
	}

	onCarouselClick(event) {
		const button = event.target.closest(
			"[data-predictive-search-carousel-prev], [data-predictive-search-carousel-next]"
		);
		if (!button) return;

		const list = button
			.closest(".predictive-search__results")
			?.querySelector(".predictive-search__results-list_products");
		if (!list) return;

		const card = list.querySelector(".predictive-search__list-item");
		const amount = card
			? card.getBoundingClientRect().width +
			  parseFloat(getComputedStyle(card).marginRight || 0) +
			  parseFloat(getComputedStyle(list).columnGap || 0)
			: list.clientWidth * 0.8;
		const direction = button.hasAttribute("data-predictive-search-carousel-prev")
			? -1
			: 1;
		list.scrollBy({ left: amount * direction, behavior: "smooth" });
	}

	getQuery() {
		return this.input.value.trim();
	}

	onChange() {
		const newSearchTerm = this.getQuery();
		if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
			// Remove the results when they are no longer relevant for the new search term
			// so they don't show up when the dropdown opens again
			this.querySelector(".predictive-search__panel")?.remove();
		}

		// Update the term asap, don't wait for the predictive search query to finish loading
		this.updateSearchForTerm(this.searchTerm, newSearchTerm);

		this.searchTerm = newSearchTerm;

		if (!this.searchTerm.length) {
			this.getUpsellResults();
			return;
		}

		this.getSearchResults(this.searchTerm);
	}

	onFormSubmit(event) {
		if (
			!this.getQuery().length ||
			this.querySelector('[aria-selected="true"] a') ||
			event.submitter.classList.contains("card__link")
		)
			event.preventDefault();
	}

	onFormReset(event) {
		super.onFormReset(event);
		if (super.shouldResetForm()) {
			this.searchTerm = "";
			this.abortController.abort();
			this.abortController = new AbortController();
			this.close(true);
		}
	}

	onFocus() {
		const currentSearchTerm = this.getQuery();
		if (this.classList.contains("search-modal__form")) {
			if (this.headerBottom) {
				this.headerBottom.classList.add("header__bottom--visible");
			}
			if (this.promoModal) {
				this.promoModal.classList.remove("search__modal--hidden");
			}
		}

		if (!currentSearchTerm.length) {
			this.getUpsellResults();
			return;
		}

		if (this.searchTerm !== currentSearchTerm) {
			// Search term was changed from other search input, treat it as a user change
			this.onChange();
		} else if (this.getAttribute("results") === "true") {
			this.open();
			if (this.promoModal) {
				this.promoModal.classList.add("search__modal--hidden");
			}
		} else {
			this.getSearchResults(this.searchTerm);
		}
	}

	onFocusOut() {
		if (this.classList.contains("search-modal__form")) {
			if (this.headerBottom) {
				this.headerBottom.classList.remove("header__bottom--visible");
			}
		}
		if (!this.classList.contains("search-modal__form")) {
			setTimeout(() => {
				if (!this.contains(document.activeElement)) this.close();
			});
		}
	}

	onBlur() {
		this.headerInput.blur();
	}

	onKeyup(event) {
		event.preventDefault();

		switch (event.code) {
			case "ArrowUp":
				this.switchOption("up");
				break;
			case "ArrowDown":
				this.switchOption("down");
				break;
			case "Enter":
				this.selectOption();
				break;
			case "Escape":
				this.close();
				break;
			default:
				if (!this.getQuery().length) this.getUpsellResults();
		}
	}

	onKeydown(event) {
		// Prevent the cursor from moving in the input when using the up and down arrow keys
		if (event.code === "ArrowUp" || event.code === "ArrowDown") {
			event.preventDefault();
		}
	}

	updateSearchForTerm(previousTerm, newTerm) {
		const searchForTextElement = this.querySelector(
			"[data-predictive-search-search-for-text]"
		);
		const currentButtonText = searchForTextElement?.innerText;
		if (currentButtonText) {
			if (
				currentButtonText.matchAll(new RegExp(previousTerm, "g")).length > 1
			) {
				// The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
				return;
			}
			const newButtonText = currentButtonText.replace(previousTerm, newTerm);
			searchForTextElement.innerText = newButtonText;
		}
	}

	switchOption(direction) {
		if (!this.hasAttribute("open")) return;

		const moveUp = direction === "up";
		const selected = this.querySelector('[role="option"][aria-selected="true"]');
		const options = Array.from(this.querySelectorAll('[role="option"]')).filter(
			(element) => element.offsetParent !== null
		);
		if (!options.length || (moveUp && !selected)) return;

		const current = options.indexOf(selected);
		let next;
		if (moveUp) {
			next = current <= 0 ? options.length - 1 : current - 1;
		} else {
			next = current === options.length - 1 ? 0 : current + 1;
		}

		const active = options[next];
		if (selected) selected.setAttribute("aria-selected", false);
		active.setAttribute("aria-selected", true);
		this.input.setAttribute("aria-activedescendant", active.id);
		active.scrollIntoView({ block: "nearest", inline: "nearest" });
	}

	selectOption() {
		const selectedOption = this.querySelector(
			'[aria-selected="true"] a, button[aria-selected="true"]'
		);

		if (selectedOption) selectedOption.click();
	}

	getSearchResults(searchTerm) {
		const queryKey = searchTerm.replace(" ", "-").toLowerCase();
		this.setLiveRegionLoadingState();

		if (this.cachedResults[queryKey]) {
			this.renderSearchResults(this.cachedResults[queryKey]);
			return;
		}

		if (this.promoModal && this.classList.contains("search-modal__form")) {
			this.promoModal.classList.add("search__modal--hidden");
		}

		fetch(
			`${routes.predictive_search_url}?q=${encodeURIComponent(
				searchTerm
			)}&resources[type]=product,query,collection,page,article&resources[limit]=10&resources[limit_scope]=each&section_id=predictive-search`,
			{ signal: this.abortController.signal }
		)
			.then((response) => {
				if (!response.ok) {
					var error = new Error(response.status);
					this.close();
					throw error;
				}
				return response.text();
			})
			.then((text) => {
				const resultsMarkup = new DOMParser()
					.parseFromString(text, "text/html")
					.querySelector("#shopify-section-predictive-search").innerHTML;
				// Save bandwidth keeping the cache in all instances synced
				this.allPredictiveSearchInstances.forEach(
					(predictiveSearchInstance) => {
						predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
					}
				);
				this.renderSearchResults(resultsMarkup);
				colorSwatches();
			})
			.catch((error) => {
				if (error?.code === 20) {
					// Code 20 means the call was aborted
					return;
				}
				this.close();
				throw error;
			});
	}

	setLiveRegionLoadingState() {
		this.statusElement =
			this.statusElement || this.querySelector(".predictive-search-status");
		this.loadingText =
			this.loadingText || this.getAttribute("data-loading-text");

		this.setLiveRegionText(this.loadingText);
		this.setAttribute("loading", true);
	}

	setLiveRegionText(statusText) {
		this.statusElement.setAttribute("aria-hidden", "false");
		this.statusElement.textContent = statusText;

		setTimeout(() => {
			this.statusElement.setAttribute("aria-hidden", "true");
		}, 1000);
	}

	renderSearchResults(resultsMarkup) {
		this.predictiveSearchResults.forEach(
			(item) => (item.innerHTML = resultsMarkup)
		);
		this.setAttribute("results", true);
		requestAnimationFrame(() => this.updateCarouselButtons());

		this.setLiveRegionResults();
		this.open();
		if (this.promoModal && this.classList.contains("search-modal__form")) {
			this.promoModal.classList.add("search__modal--hidden");
		}
	}

	// The Figma design shows the same 2-column panel (popular-searches/popular-items
	// pills + promo banner on the left, a product heading+grid on the right) in all
	// 3 states. When there's no query, predictive_search_url can't be used (it
	// requires a search term), so this fetches the search route instead — the same
	// section renders there with predictive_search.performed simply false, which
	// predictive-search.liquid uses to pick its "You May Like" branch.
	fetchUpsell() {
		const queryKey = "__upsell__";
		if (this.cachedResults[queryKey]) {
			return Promise.resolve(this.cachedResults[queryKey]);
		}

		return fetch(`${routes.search_url}?section_id=predictive-search`, {
			signal: this.abortController.signal,
		})
			.then((response) => {
				if (!response.ok) {
					throw new Error(response.status);
				}
				return response.text();
			})
			.then((text) => {
				const resultsMarkup = new DOMParser()
					.parseFromString(text, "text/html")
					.querySelector("#shopify-section-predictive-search").innerHTML;
				this.allPredictiveSearchInstances.forEach((instance) => {
					instance.cachedResults[queryKey] = resultsMarkup;
				});
				return resultsMarkup;
			});
	}

	preloadUpsell() {
		if (this.allPredictiveSearchInstances[0] !== this) return;
		setTimeout(() => this.fetchUpsell().catch(() => {}), 100);
	}

	getUpsellResults() {
		this.setLiveRegionLoadingState();
		this.fetchUpsell()
			.then((resultsMarkup) => {
				this.renderSearchResults(resultsMarkup);
				colorSwatches();
			})
			.catch((error) => {
				if (error?.code === 20) {
					return;
				}
				throw error;
			});
	}

	setLiveRegionResults() {
		this.removeAttribute("loading");
		this.setLiveRegionText(
			this.querySelector("[data-predictive-search-live-region-count-value]")
				.textContent
		);
	}

	open() {
		this.setAttribute("open", true);
		this.input.setAttribute("aria-expanded", true);
		this.isOpen = true;
		this.parentElement.parentElement.parentElement.classList.add(
			"predictive-open"
		);
		this.toggleBodyLock(true);
		// The header's results panel and the mega menu are both full-width panels under
		// the header, so close the menu (see header-mobilia.js) instead of overlapping it.
		if (this.querySelector(".predictive-search--header-mb")) {
			document.dispatchEvent(new CustomEvent("header:close-mega"));
		}
		if (this.promoModal && this.classList.contains("search-modal__form")) {
			this.promoModal.classList.remove("search__modal--hidden");
		}
	}

	toggleBodyLock(lock) {
		if (!this.querySelector(".predictive-search--header-mb")) return;
		document.body.classList.toggle("predictive-search-open", lock);
	}

	close(clearSearchTerm = false) {
		this.closeResults(clearSearchTerm);
		this.parentElement.parentElement.parentElement.classList.remove(
			"predictive-open"
		);
		this.toggleBodyLock(false);
		this.isOpen = false;
	}

	closeResults(clearSearchTerm = false) {
		if (clearSearchTerm) {
			this.input.value = "";
			this.removeAttribute("results");
		}
		const selected = this.querySelector('[aria-selected="true"]');

		if (selected) selected.setAttribute("aria-selected", false);

		this.input.setAttribute("aria-activedescendant", "");
		this.removeAttribute("loading");
		this.removeAttribute("open");
		this.input.setAttribute("aria-expanded", false);
		//this.predictiveSearchResults.removeAttribute("style");
		this.predictiveSearchResults.forEach((item) =>
			item.removeAttribute("style")
		);

		if (this.promoModal) {
			this.promoModal.classList.add("search__modal--hidden");
		}
	}
}

customElements.define("predictive-search", PredictiveSearch);
